/**
 * Route guard. The decision table lives here rather than being scattered
 * through pages, because the interesting cases are the unhappy ones and they
 * are easy to get subtly wrong.
 *
 *   authStatus   profileStatus      result
 *   ----------   ----------------   ------------------------------------------
 *   loading      -                  loading state
 *   signed-out   -                  -> /login (remembering where you were)
 *   signed-in    idle | loading     loading state
 *   signed-in    not-provisioned    -> /no-profile
 *   signed-in    inactive           -> /no-profile (different message there)
 *   signed-in    error              error + retry, NOT a sign-out
 *   signed-in    ok                 the page
 *
 * The error row matters: a Railway hiccup or a dropped connection must never
 * present as being logged out. Being ejected to a login screen by a transient
 * server error, mid-task, is how people lose trust in an application.
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './useAuth.js';
import ErrorBanner from '../components/ErrorBanner.jsx';

function Loading({ label }) {
  return (
    <div className="centered-page" aria-busy="true" aria-live="polite">
      <p className="muted">{label}</p>
    </div>
  );
}

export function ProtectedRoute({ children }) {
  const { authStatus, profileStatus, profileError, retryProfile } = useAuth();
  const location = useLocation();

  if (authStatus === 'loading') {
    return <Loading label="Checking your sign-in…" />;
  }

  if (authStatus === 'signed-out') {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (profileStatus === 'idle' || profileStatus === 'loading') {
    return <Loading label="Loading your account…" />;
  }

  if (profileStatus === 'not-provisioned' || profileStatus === 'inactive') {
    return <Navigate to="/no-profile" replace />;
  }

  if (profileStatus === 'error') {
    return (
      <div className="centered-page">
        <h1>Couldn’t load your account</h1>
        <ErrorBanner error={profileError} />
        <p className="muted">
          You are still signed in. This is usually a temporary problem with the
          Attest server.
        </p>
        <button type="button" className="btn" onClick={retryProfile}>
          Try again
        </button>
      </div>
    );
  }

  return children;
}

export default ProtectedRoute;
